import { useState } from 'react'
import { GenericObject } from '../types/typeAliases'

type Update<T> = (key: keyof T, value: unknown) => void

export const nameSession = (name: string): string => {
  const { host, pathname } = window.location
  return `${host}${pathname}-${name}`
}

export default function useSession<T>(
  sessionName: string,
  init?: T,
  forgetful?: boolean,
): [T, (newState: T) => void, Update<T>] {
  const scopedName = nameSession(sessionName)

  const initState = sessionStorage.getItem(scopedName)
  const [sessionState, setState] = useState<T>(
    initState && !forgetful ? JSON.parse(initState) : init,
  )

  const setSessionState = (newState: T): void => {
    if (!forgetful) {
      if (newState) {
        sessionStorage.setItem(scopedName, JSON.stringify(newState))
      } else {
        sessionStorage.removeItem(scopedName)
      }
    }
    setState(newState)
  }

  const updateSessionState: Update<T> = (key, value): void => {
    setState((prevState) => {
      const newState = { ...prevState, [key]: value }
      if (!forgetful) {
        if (newState) {
          sessionStorage.setItem(scopedName, JSON.stringify(newState))
        } else {
          sessionStorage.removeItem(scopedName)
        }
      }
      return newState
    })
  }

  return [sessionState, setSessionState, updateSessionState]
}

export const clearSession = (name: string): void => {
  const { host, pathname } = window.location
  sessionStorage.removeItem(`${host}${pathname}-${name}`)
}

export const clearAllSessions = (nameEnum: GenericObject<string>): void => {
  Object.values(nameEnum).forEach(clearSession)
}
